import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { PlayerCandidate } from '../types';

async function compressAuctionImage(file: File): Promise<File> {
  const maxSide = 1400;
  const targetBytes = 900 * 1024;

  if (!file.type.startsWith('image/')) return file;

  const imageUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Immagine non valida.'));
      img.src = imageUrl;
    });

    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const toBlob = (quality: number) => new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/webp', quality);
    });

    let blob: Blob | null = null;
    for (const quality of [0.86, 0.78, 0.7, 0.62]) {
      blob = await toBlob(quality);
      if (blob && blob.size <= targetBytes) break;
    }

    if (!blob) return file;
    if (blob.size >= file.size && file.size <= targetBytes) return file;

    const cleanName = file.name.replace(/\.[^.]+$/, '') || 'asta';
    return new File([blob], `${cleanName}.webp`, { type: 'image/webp' });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function integerValue(value: string, fallback: number): number {
  const parsed = Number(value.replace(',', '.'));
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(fallback, Math.floor(parsed));
}

export default function CreateAuctionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [playerDataEnabled, setPlayerDataEnabled] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [playerCandidates, setPlayerCandidates] = useState<PlayerCandidate[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerCandidate | null>(null);
  const [searchingPlayer, setSearchingPlayer] = useState(false);
  const [playerSearchError, setPlayerSearchError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [startingPrice, setStartingPrice] = useState('1');
  const [minIncrement, setMinIncrement] = useState('1');
  const [closesAt, setClosesAt] = useState('');
  const [antiSnipingWindow, setAntiSnipingWindow] = useState('2');
  const [extensionMinutes, setExtensionMinutes] = useState('2');
  const [chatLockedOnClose, setChatLockedOnClose] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'player_data_enabled').maybeSingle()
      .then(({ data }) => setPlayerDataEnabled(data?.value === true));
  }, []);

  const searchPlayer = async () => {
    const query = playerName.trim();
    if (query.length < 3) {
      setPlayerSearchError('Inserisci almeno 3 caratteri.');
      return;
    }
    setSearchingPlayer(true);
    setPlayerSearchError(null);
    setSelectedPlayer(null);
    try {
      const { data, error } = await supabase.functions.invoke('player-search', { body: { query } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const candidates = (data?.candidates || []) as PlayerCandidate[];
      setPlayerCandidates(candidates);
      if (candidates.length === 1) setSelectedPlayer(candidates[0]);
      if (candidates.length === 0) setPlayerSearchError('Nessun giocatore trovato. Puoi creare comunque l’asta senza statistiche.');
    } catch (err) {
      setPlayerSearchError(err instanceof Error ? err.message : 'Ricerca non riuscita.');
      setPlayerCandidates([]);
    } finally {
      setSearchingPlayer(false);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!file || !user) return null;
    const compressed = await compressAuctionImage(file);
    const extension = compressed.name.split('.').pop() || 'webp';
    const path = `${user.id}/${crypto.randomUUID()}.${extension}`;
    const { error: uploadError } = await supabase.storage.from('auction-images').upload(path, compressed, {
      cacheControl: '31536000',
      upsert: false,
      contentType: compressed.type || 'image/webp'
    });
    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from('auction-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const createAuction = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;

    setBusy(true);
    setError(null);
    try {
      const imageUrl = await uploadImage();
      let externalPlayerId: string | null = null;
      if (playerDataEnabled && selectedPlayer) {
        const { data: playerData, error: playerError } = await supabase.functions.invoke('player-select', { body: { candidate: selectedPlayer } });
        if (playerError) throw playerError;
        if (playerData?.error) throw new Error(playerData.error);
        externalPlayerId = playerData?.player_id || null;
      }
      const closeDate = new Date(closesAt);
      if (Number.isNaN(closeDate.getTime())) throw new Error('Data chiusura non valida.');

      const { data, error: insertError } = await supabase
        .from('auctions')
        .insert({
          created_by: user.id,
          title: title.trim(),
          description: description.trim(),
          image_url: imageUrl,
          starting_price: integerValue(startingPrice, 0),
          min_increment: Math.max(1, integerValue(minIncrement, 1)),
          closes_at: closeDate.toISOString(),
          auto_extend_enabled: true,
          anti_sniping_window_minutes: Math.max(1, integerValue(antiSnipingWindow, 1)),
          extension_minutes: Math.max(1, integerValue(extensionMinutes, 1)),
          chat_locked_on_close: chatLockedOnClose,
          external_player_id: externalPlayerId
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Recupera subito i dati del solo giocatore selezionato. Se il provider non
      // risponde, l'asta viene comunque creata e il cron riproverà più tardi.
      if (externalPlayerId) {
        try {
          await Promise.race([
            supabase.functions.invoke('update-player-data', { body: { player_id: externalPlayerId } }),
            new Promise((resolve) => window.setTimeout(resolve, 12000)),
          ]);
        } catch (refreshError) {
          console.warn('Aggiornamento immediato dati giocatore non riuscito:', refreshError);
        }
      }

      navigate(`/auctions/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Creazione asta non riuscita.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-stack narrow">
      <section className="panel">
        <span className="eyebrow">Nuova asta</span>
        <h1>Crea asta</h1>
        <p className="muted">Imposta rilancio minimo, timer e anti-sniping.</p>

        <form className="form-stack create-form" onSubmit={createAuction}>
          <label>Titolo<input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} maxLength={120} /></label>

          {playerDataEnabled && (
            <div className="player-search-box">
              <label>Nome del giocatore
                <div className="player-search-row">
                  <input
                    value={playerName}
                    onChange={(e) => { setPlayerName(e.target.value); setSelectedPlayer(null); setPlayerCandidates([]); }}
                    placeholder="Es. Lautaro Martinez"
                    minLength={3}
                  />
                  <button type="button" className="ghost-button" onClick={searchPlayer} disabled={searchingPlayer}>
                    {searchingPlayer ? 'Cerco…' : 'Cerca'}
                  </button>
                </div>
              </label>
              <p className="muted form-hint">Scrivi solo il nome e scegli il risultato corretto. L’asta può essere creata anche senza collegare un giocatore.</p>
              {playerCandidates.length > 0 && (
                <div className="player-candidates">
                  {playerCandidates.map((candidate) => (
                    <button
                      type="button"
                      key={candidate.provider_player_id}
                      className={selectedPlayer?.provider_player_id === candidate.provider_player_id ? 'player-candidate selected' : 'player-candidate'}
                      onClick={() => setSelectedPlayer(candidate)}
                    >
                      {candidate.photo_url && <img src={candidate.photo_url} alt="" />}
                      <span><strong>{candidate.full_name}</strong><small>{candidate.team_name || 'Squadra non disponibile'} · {candidate.position || 'Ruolo non disponibile'}</small></span>
                    </button>
                  ))}
                </div>
              )}
              {selectedPlayer && <p className="success-text">Selezionato: {selectedPlayer.full_name} ({selectedPlayer.team_name || 'squadra non disponibile'})</p>}
              {playerSearchError && <p className="error-text">{playerSearchError}</p>}
            </div>
          )}
          <label>Descrizione<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} /></label>

          <label className="file-input">
            <ImagePlus size={22} />
            <span>{file ? `${file.name} · compressione automatica attiva` : 'Aggiungi foto'}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>

          <div className="form-grid">
            <label>Prezzo iniziale (€ interi)<input inputMode="numeric" pattern="[0-9]*" step="1" value={startingPrice} onChange={(e) => setStartingPrice(e.target.value.replace(/[^0-9]/g, ''))} required /></label>
            <label>Rilancio minimo (€ interi)<input inputMode="numeric" pattern="[0-9]*" step="1" min="1" value={minIncrement} onChange={(e) => setMinIncrement(e.target.value.replace(/[^0-9]/g, ''))} required /></label>
          </div>

          <label>Data e ora chiusura<input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} required /></label>

          <div className="form-grid">
            <label>Se offerta entro gli ultimi minuti<input type="number" min="1" max="120" required value={antiSnipingWindow} onChange={(e) => setAntiSnipingWindow(e.target.value)} /></label>
            <label>Allunga il timer di minuti<input type="number" min="1" max="120" required value={extensionMinutes} onChange={(e) => setExtensionMinutes(e.target.value)} /></label>
          </div>
          <p className="muted form-hint">Il prolungamento è sempre attivo. Il valore minimo è 1 minuto per entrambi i campi.</p>

          <label className="setting-row in-form">
            <span>Blocca chat alla chiusura</span>
            <input type="checkbox" checked={chatLockedOnClose} onChange={(e) => setChatLockedOnClose(e.target.checked)} />
          </label>

          <button className="primary-button full" disabled={busy}>{busy ? 'Creo asta…' : 'Crea asta'}</button>
          {error && <p className="error-text">{error}</p>}
        </form>
      </section>
    </div>
  );
}
